/**
 * PipelineOverviewStrip — horizontal phase-grouped stepper showing aggregate
 * stage status across all (creative, persona) pairs for a run.
 *
 * Rendered above the tabs in RunDetailView when pipeline data is available.
 * Clicking a stage chip opens StageDetailModal.
 */
import { useState } from 'react';
import { clsx } from 'clsx';
import type { CreativeReviewPipelineState, PipelineStatus, StageId } from '@/services/api';
import { PHASE_MANIFEST, STAGE_MAP } from '../pipelineManifest';
import { PipelineStageChip } from './PipelineStageChip';
import { StageDetailModal } from './StageDetailModal';

interface Props {
  pipelines: CreativeReviewPipelineState[];
  isRunning?: boolean;
}

/** Aggregate counts for a stage across all pairs. */
interface StageCounts {
  completed: number;
  failed: number;
  skipped: number;
  running: number;
  pending: number;
  total: number;
}

function aggregateStage(
  pipelines: CreativeReviewPipelineState[],
  stageId: StageId,
): StageCounts {
  const counts: StageCounts = {
    completed: 0, failed: 0, skipped: 0, running: 0, pending: 0, total: pipelines.length,
  };
  for (const p of pipelines) {
    const stage = p.stages?.[stageId];
    const st = (stage?.status ?? 'pending') as PipelineStatus;
    if (st === 'completed') counts.completed++;
    else if (st === 'failed') counts.failed++;
    else if (st === 'skipped') counts.skipped++;
    else if (st === 'running') counts.running++;
    else counts.pending++;
  }
  return counts;
}

function dominantStatus(counts: StageCounts): PipelineStatus {
  if (counts.failed > 0 && counts.completed === 0 && counts.running === 0) return 'failed';
  if (counts.running > 0) return 'running';
  if (counts.completed > 0 && counts.failed === 0 && counts.pending === 0 && counts.running === 0) {
    return counts.skipped === counts.total ? 'skipped' : 'completed';
  }
  if (counts.completed > 0 || counts.failed > 0) return 'running'; // partial = in-progress
  return 'pending';
}

export function PipelineOverviewStrip({ pipelines, isRunning }: Props) {
  const [selectedStage, setSelectedStage] = useState<StageId | null>(null);

  if (pipelines.length === 0 && !isRunning) return null;

  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 mb-4 overflow-x-auto">
        <div className="flex items-start gap-1 min-w-max">
          {PHASE_MANIFEST.map((phase, phaseIdx) => {
            return (
              <div key={phase.phase_id} className="flex items-start gap-1">
                {/* Phase separator */}
                {phaseIdx > 0 && (
                  <div className="self-center text-slate-300 mx-1 text-xs">▶</div>
                )}
                {/* Phase group */}
                <div className="flex flex-col gap-1">
                  <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-1">
                    {phase.label}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {phase.stages.map((stageId) => {
                      const entry = STAGE_MAP[stageId as StageId];
                      if (!entry) return null;
                      const counts = aggregateStage(pipelines, stageId as StageId);
                      const status = dominantStatus(counts);
                      const showCount = pipelines.length > 0;
                      return (
                        <PipelineStageChip
                          key={stageId}
                          label={entry.label}
                          status={status}
                          count={showCount && status === 'running' ? counts.running : undefined}
                          onClick={() => setSelectedStage(stageId as StageId)}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Live pulse when run is in progress */}
          {isRunning && pipelines.length === 0 && (
            <div className="self-center ml-2 flex items-center gap-1.5 text-xs text-blue-500">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-400 animate-ping" />
              Waiting for first pair…
            </div>
          )}
        </div>

        {/* Progress summary line */}
        {pipelines.length > 0 && (
          <div className="mt-2 text-xs text-slate-500">
            {pipelines.filter(p => p.status === 'completed').length} /{' '}
            {pipelines.length} pairs complete
            {isRunning && (
              <span className="ml-1.5 text-blue-500 animate-pulse">· running</span>
            )}
          </div>
        )}
      </div>

      {selectedStage && (
        <StageDetailModal
          stageId={selectedStage}
          pipelines={pipelines}
          onClose={() => setSelectedStage(null)}
        />
      )}
    </>
  );
}
